import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ServiceService } from '../service.service';

// Tell TS about global "google"
declare const google: any;

// 👉 Use the SAME key you already use in edit-department page
const GOOGLE_MAPS_API_KEY = 'AIzaSyBOQ9-zria589tItgtGZsbzwORm6T5_c5U';

@Component({
  selector: 'app-department-detail',
  templateUrl: './department-detail.component.html',
  styleUrls: ['./department-detail.component.css'],
})
export class DepartmentDetailComponent implements OnInit {
  department: any;
  filteredWeeklySchedule: any[] = [];
  isLoading = false;
  loadError: string | null = null;
  departmentId: string | null = null;
  // Map data
  center: { lat: number; lng: number } = { lat: 0, lng: 0 };
  zoom = 15;
  map_lat = 0;
  map_log = 0;
  markers: any[] = [];

  // Popup state
  showLocationPopup = false;

  // Script loader state
  private mapsLoaded = false;

  constructor(
    private route: ActivatedRoute,
    private apiService: ServiceService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.departmentId = this.route.snapshot.queryParamMap.get('id');
    if (this.departmentId) {
      this.loadDepartmentDetails(this.departmentId);
    }
  }

  // =========================
  // Load department details
  // =========================
  loadDepartmentDetails(departmentId: string): void {
    this.isLoading = true;
    this.loadError = null;

    this.apiService.ViewDepartmentID(departmentId).subscribe({
      next: (res: any) => {
        this.isLoading = false;

        if (res && res.success && res.data && res.data.length > 0) {
          this.department = res.data[0];

          this.filteredWeeklySchedule = Array.isArray(this.department.weekly_time_schedule)
            ? this.department.weekly_time_schedule
                .slice()
                .sort((a: any, b: any) => a.day - b.day)
                .map((sched: any) => sched.isActive
                  ? sched
                  : { ...sched, workin_time: '', workout_time: '', lunch_in_time: '', lunch_out_time: '' }
                )
            : [];

          // Initialise coordinates (if present)
          this.map_lat = Number(this.department.department_latitude) || 0;
          this.map_log = Number(this.department.department_longitude) || 0;

          if (this.map_lat && this.map_log) {
            this.center = { lat: this.map_lat, lng: this.map_log };
            this.markers = [
              {
                position: { lat: this.map_lat, lng: this.map_log },
                label: 'Department location',
                title: 'Department location',
                options: {
                  animation: google?.maps?.Animation?.DROP,
                },
              },
            ];
          }
        } else {
          this.loadError = 'Failed to load department details.';
        }
      },
      error: (err: any) => {
        console.error('Error fetching department details:', err);
        this.isLoading = false;
        this.loadError = 'Error fetching department details from the server.';
      },
    });
  }

  // =========================
  // GOOGLE MAPS SCRIPT LOADER
  // =========================
  private loadGoogleMaps(): Promise<void> {
  return new Promise((resolve, reject) => {
    // Already loaded?
    if ((window as any).google?.maps) {
      resolve();
      return;
    }

    const scriptId = 'google-maps-script';
    const existingScript = document.getElementById(scriptId) as HTMLScriptElement;

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve());
      existingScript.addEventListener('error', reject);
      return;
    }

    // Create global callback (Google will call this)
    (window as any).initDeptDetailMap = () => {
      resolve();
    };

    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;

    // You can add &libraries=places if you want, not required for basic map
    script.src =
      `https://maps.googleapis.com/maps/api/js` +
      `?key=${GOOGLE_MAPS_API_KEY}` +
      `&callback=initDeptDetailMap&v=weekly`;

    script.onerror = (err) => {
      console.error('Failed to load Google Maps script', err);
      reject(err);
    };

    document.body.appendChild(script);
  });
}


  // =========================
  // Location popup
  // =========================
  openLocationPopup(): void {
    // If no coordinates, nothing to show
    this.map_lat = Number(this.department?.department_latitude) || 0;
    this.map_log = Number(this.department?.department_longitude) || 0;

    if (!this.map_lat || !this.map_log) {
      return;
    }

    this.loadGoogleMaps()
      .then(() => {
        this.center = { lat: this.map_lat, lng: this.map_log };
        this.markers = [
          {
            position: { lat: this.map_lat, lng: this.map_log },
            label: 'Department location',
            title: 'Department location',
            options: {
              animation: google.maps.Animation.DROP,
            },
          },
        ];

        // Show popup only AFTER script is ready
        this.showLocationPopup = true;
      })
      .catch(() => {
        // Optional: set an error message if map fails
      });
  }

  closeLocationPopup(): void {
    this.showLocationPopup = false;
  }

  // =========================
  // Edit navigation
  // =========================
  editDepartment(departmentId: string): void {
    this.router.navigate(['/admin/department-edit-page'], {
      queryParams: { id: departmentId },
    });
  }

  formatTime24(value: string | null | undefined): string {
    if (!value) return '-';

    const trimmed = value.trim();

    const match = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
    if (match) {
      let hours = parseInt(match[1], 10);
      const minutes = match[2];
      const period = match[3].toUpperCase();

      if (period === 'AM' && hours === 12) hours = 0;
      if (period === 'PM' && hours !== 12) hours += 12;

      return `${String(hours).padStart(2, '0')}:${minutes}`;
    }

        const already24 = trimmed.match(/^([01]?\d|2[0-3]):([0-5]\d)(:[0-5]\d)?$/);
    if (already24) {
      return `${already24[1].padStart(2, '0')}:${already24[2]}`;
    }

    return trimmed;
  }

  formatDayName(day: string): string {
    const dayMap: { [key: string]: string } = {
      'sunday': 'Sunday',
      'monday': 'Monday',
      'tuesday': 'Tuesday',
      'wednesday': 'Wednesday',
      'wenday': 'Wednesday',
      'thursday': 'Thursday',
      'friday': 'Friday',
      'saturday': 'Saturday'
    };
    return dayMap[day?.toLowerCase()] || day;
  }

  formatDayNumber(day: number): string {
    const dayNumberMap: { [key: number]: string } = {
      1: 'Sunday',
      2: 'Monday',
      3: 'Tuesday',
      4: 'Wednesday',
      5: 'Thursday',
      6: 'Friday',
      7: 'Saturday'
    };
    return dayNumberMap[day] || String(day);
  }
}     
